// EYB diagnostic questionnaire — 12 categories.
// Each question has an id, type, and tag(s) used by the scope engine.

export const CATEGORIES = [
  {
    id: "cash_flow",
    label: "Cash Flow",
    intro: "How predictable is the money moving through your business?",
    questions: [
      {
        id: "cf_forecast",
        type: "choice",
        prompt: "Do you currently prepare cash flow forecasts?",
        options: [
          { value: "no", label: "No, we don't forecast cash flow", weight: 3, tags: ["cash_flow_gap"] },
          { value: "informal", label: "Informally, in my head or on a spreadsheet", weight: 2, tags: ["cash_flow_gap"] },
          { value: "monthly", label: "Yes, monthly", weight: 1 },
          { value: "rolling", label: "Yes, rolling 13-week forecast", weight: 0 },
        ],
      },
      {
        id: "cf_confidence",
        type: "slider",
        prompt: "How confident are you in your ability to pay bills over the next 90 days?",
        min: 1, max: 10, step: 1,
        lowLabel: "Not confident", highLabel: "Very confident",
        scoreInverse: true, weight: 0.4, tag: "cash_flow_gap",
      },
      {
        id: "cf_debtors",
        type: "yesno",
        prompt: "Do you struggle with late-paying customers or unpaid invoices?",
        yesTags: ["debtor_issues"],
        yesWeight: 2,
      },
    ],
  },
  {
    id: "profitability",
    label: "Profitability",
    intro: "Do you know where your business is actually making money?",
    questions: [
      {
        id: "pr_margins",
        type: "choice",
        prompt: "Do you know your gross profit margins by product or service?",
        options: [
          { value: "no", label: "No, not really", weight: 3, tags: ["margin_unclear"] },
          { value: "overall", label: "Only the overall margin", weight: 2, tags: ["margin_unclear"] },
          { value: "yes", label: "Yes, by product/service line", weight: 0 },
        ],
      },
      {
        id: "pr_review",
        type: "yesno",
        prompt: "Have you reviewed your profitability in the last 12 months?",
        noTags: ["margin_unclear"],
        noWeight: 2,
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    intro: "Is your pricing earning what your work is worth?",
    questions: [
      {
        id: "pc_confidence",
        type: "slider",
        prompt: "How confident are you in your pricing model?",
        min: 1, max: 10, step: 1,
        lowLabel: "Not confident", highLabel: "Very confident",
        scoreInverse: true, weight: 0.4, tag: "pricing_uncertain",
      },
      {
        id: "pc_review",
        type: "choice",
        prompt: "When did you last review your pricing?",
        options: [
          { value: "never", label: "Never formally reviewed", weight: 3, tags: ["pricing_uncertain"] },
          { value: "12m+", label: "More than 12 months ago", weight: 2, tags: ["pricing_uncertain"] },
          { value: "recent", label: "Within the last 12 months", weight: 0 },
        ],
      },
    ],
  },
  {
    id: "funding",
    label: "Funding Readiness",
    intro: "Planning to raise capital or apply for finance?",
    questions: [
      {
        id: "fd_plans",
        type: "yesno",
        prompt: "Are you planning to apply for funding in the next 12 months?",
        yesTags: ["funding_planned"],
        yesWeight: 3,
      },
      {
        id: "fd_forecasts",
        type: "yesno",
        prompt: "Do you have current 3-year financial forecasts ready?",
        noTags: ["funding_planned"],
        noWeight: 1,
      },
    ],
  },
  {
    id: "systems",
    label: "Systems & Processes",
    intro: "Are the engines of your business running cleanly?",
    questions: [
      {
        id: "sy_software",
        type: "choice",
        prompt: "How would you describe your accounting/financial systems?",
        options: [
          { value: "messy", label: "Disorganised — I avoid logging in", weight: 3, tags: ["systems_issues"] },
          { value: "working", label: "Working but underutilised", weight: 2, tags: ["systems_issues"] },
          { value: "clean", label: "Clean and well-maintained", weight: 0 },
        ],
      },
      {
        id: "sy_dashboards",
        type: "yesno",
        prompt: "Do you have a regular KPI dashboard you actually look at?",
        noTags: ["systems_issues"],
        noWeight: 2,
      },
    ],
  },
  {
    id: "reporting",
    label: "Reporting",
    intro: "How clearly can you see what's happening in your numbers?",
    questions: [
      {
        id: "rp_frequency",
        type: "choice",
        prompt: "How often do you review formal financial reports?",
        options: [
          { value: "rarely", label: "Rarely or only at year-end", weight: 3, tags: ["reporting_gap"] },
          { value: "quarterly", label: "Quarterly", weight: 1 },
          { value: "monthly", label: "Monthly", weight: 0 },
        ],
      },
    ],
  },
  {
    id: "staffing",
    label: "Staffing",
    intro: "Do you have the right people in the right roles?",
    questions: [
      {
        id: "st_challenge",
        type: "yesno",
        prompt: "Is staffing or team capacity a current concern?",
        yesTags: ["staffing_concerns"],
        yesWeight: 2,
      },
    ],
  },
  {
    id: "growth",
    label: "Growth Plans",
    intro: "Where do you want this business to be in 3 years?",
    questions: [
      {
        id: "gr_plans",
        type: "choice",
        prompt: "Do you have a clear growth plan for the next 1–3 years?",
        options: [
          { value: "no", label: "No, mostly reactive day-to-day", weight: 3, tags: ["growth_planning"] },
          { value: "ideas", label: "Some ideas, but not written down", weight: 2, tags: ["growth_planning"] },
          { value: "plan", label: "Yes, a documented plan", weight: 0 },
        ],
      },
    ],
  },
  {
    id: "understanding",
    label: "Financial Understanding",
    intro: "How comfortable are you reading the story your numbers tell?",
    questions: [
      {
        id: "un_confidence",
        type: "slider",
        prompt: "How confident are you in interpreting your financial reports?",
        min: 1, max: 10, step: 1,
        lowLabel: "Not confident", highLabel: "Very confident",
        scoreInverse: true, weight: 0.4, tag: "low_confidence",
      },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    intro: "Are you sleeping well at night when it comes to tax & regulation?",
    questions: [
      {
        id: "cp_concerns",
        type: "yesno",
        prompt: "Do you have any current concerns about tax, BAS, or regulatory compliance?",
        yesTags: ["compliance_concern"],
        yesWeight: 2,
      },
    ],
  },
  {
    id: "planning",
    label: "Business Planning",
    intro: "How much of your strategy lives in your head versus on paper?",
    questions: [
      {
        id: "pl_plan",
        type: "yesno",
        prompt: "Do you currently have a written business plan?",
        noTags: ["planning_gap"],
        noWeight: 2,
      },
    ],
  },
  {
    id: "mentoring",
    label: "Mentoring",
    intro: "Where do you turn when you need to talk through big decisions?",
    questions: [
      {
        id: "mn_support",
        type: "yesno",
        prompt: "Would you benefit from a regular advisor to talk through business decisions?",
        yesTags: ["mentoring_need"],
        yesWeight: 2,
      },
      {
        id: "mn_biggest",
        type: "text",
        prompt: "In one or two sentences — what is the biggest challenge in your business right now?",
        placeholder: "e.g. We're growing but the cash never seems to keep up...",
        optional: true,
      },
    ],
  },
];

export const REVENUE_RANGES = [
  "Under $250k",
  "$250k – $500k",
  "$500k – $1M",
  "$1M – $2M",
  "$2M – $5M",
  "$5M – $10M",
  "$10M+",
];

export const EMPLOYEE_RANGES = [
  "Just me",
  "2 – 5",
  "6 – 15",
  "16 – 50",
  "50+",
];

export const ACCOUNTING_SOFTWARE = [
  "Xero",
  "MYOB",
  "QuickBooks",
  "Reckon",
  "Spreadsheets",
  "None / Not sure",
  "Other",
];

export const AUSTRALIAN_STATES = [
  "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT", "Other",
];
